import React, { useState, useEffect } from 'react';
import { SaveChangesConfirmation } from '@components/SaveChangesConfirmation';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { Axios, getErrorMessage } from '@helpers/ApiService';
import { useToast } from '@contexts/Toast';
import { ValidationService } from '@services/ValidationService';
import { Address, AddressData } from '@helpers/Models';
import { responseTexts } from '@helpers/Constants';
import { getUserInitial } from '@services/UserService';
import { AxiosError } from 'axios';

type ConfirmationType = 'personal' | 'password';

export const PersonalDetails: React.FC = () => {
  const [isPersonalDetailsEditing, setIsPersonalDetailsEditing] =
    useState(false);
  const [isPasswordEditing, setIsPasswordEditing] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [, setConfirmationType] = useState<ConfirmationType | null>(null);
  const { showToastMessage } = useToast();
  const [initials, setInitials] = useState<string>('');

  // User address state
  const initialAddress: Address = {
    storeName: '',
    contactName: '',
    address1: '',
    address2: '',
    address3: '',
    city: { id: 0, cityName: '' },
    state: { id: 0, stateName: '' },
    postalCode: { id: 0, postalCode: '' },
  };

  const [userAddress, setUserAddress] = useState<Address>(initialAddress);
  const [loading, setLoading] = useState(false);

  // User details state
  const [userDetails, setUserDetails] = useState<{
    name: string;
    email: string;
    phone: string;
  }>({
    name: '',
    email: '',
    phone: '',
  });

  // Store details state
  const [storeDetails, setStoreDetails] = useState<{
    companyName: string;
    address: Address;
  }>({
    companyName: '',
    address: initialAddress,
  });

  const [changePasswordObject, setChangePasswordObject] = useState<{
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  }>({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  /**
   * State controlling visibility of password fields.
   *
   * Each property determines whether the corresponding password input field
   * is displayed in plain text (`true`) or masked (`false`).
   *
   * @state
   * @property {boolean} oldPassword - Visibility of the old password field.
   * @property {boolean} newPassword - Visibility of the new password field.
   * @property {boolean} confirmPassword - Visibility of the confirm password field.
   */
  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  /**
   * State for storing validation errors for password fields.
   *
   * Each property contains the error message for the corresponding
   * password field, or an empty string if the field is valid.
   *
   * @state
   * @property {string} oldPassword - Error message for old password field.
   * @property {string} newPassword - Error message for new password field.
   * @property {string} confirmPassword - Error message for confirm password field.
   */
  const [passwordErrors, setPasswordErrors] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Fetch user address data on component mount
  useEffect(() => {
    fetchUserAddress();
  }, []);

  /**
   * Fetches the user's address data from the API
   * Uses the store address as the user's personal address
   */
  const fetchUserAddress = async () => {
    try {
      setLoading(true);
      const response = await Axios.get(ApiEndpoints.FETCH_ADDRESS);

      if (response.data.status === 'success') {
        const { addresses, userDetails: fetchedUserDetails } =
          response.data.data;
        const { storeAddress } = addresses;

        // Set user details from API response
        if (fetchedUserDetails) {
          setUserDetails({
            name: fetchedUserDetails.name || '',
            email: fetchedUserDetails.email || '',
            phone: fetchedUserDetails.phone || '',
          });
          setInitials(getUserInitial(fetchedUserDetails.name));
        }

        if (storeAddress && storeAddress.length > 0) {
          const address = storeAddress[0];

          // Fetch additional address details to get state and city names
          const addressDetailsResponse = await Axios.post(
            ApiEndpoints.GET_STATE_CITY_POSTAL,
            {
              stateId: address.stateId,
              cityId: address.cityId,
              postalCodeId: address.postalCodeId,
            },
          );

          if (addressDetailsResponse.data.status === 'success') {
            const addressData: AddressData = addressDetailsResponse.data.data;
            const stateName =
              addressData.states?.find((s) => s.id === address.stateId)
                ?.stateName || '';
            const cityName =
              addressData.cities?.find((c) => c.id === address.cityId)
                ?.cityName || '';
            const postalCode =
              addressData.postalCodes?.find(
                (p) => p.id === address.postalCodeId,
              )?.postalCode || '';

            // Set user personal address (for Personal Details section)
            setUserAddress({
              storeName: addresses?.companyName || '',
              contactName: fetchedUserDetails?.name || '',
              address1: address.address1 || '',
              address2: address.address2 || '',
              address3: address.address3 || '',
              city: { id: address.cityId || 0, cityName: cityName },
              state: { id: address.stateId || 0, stateName: stateName },
              postalCode: {
                id: address.postalCodeId || 0,
                postalCode: postalCode,
              },
            });

            // Set store details (for Store Details section)
            setStoreDetails({
              companyName: addresses.companyName || '',
              address: {
                storeName: addresses?.companyName || '',
                contactName:
                  fetchedUserDetails?.name || addresses?.contactName || '',
                address1: address.address1 || '',
                address2: address.address2 || '',
                address3: address.address3 || '',
                city: { id: address.cityId || 0, cityName: cityName },
                state: { id: address.stateId || 0, stateName: stateName },
                postalCode: {
                  id: address.postalCodeId || 0,
                  postalCode: postalCode,
                },
              },
            });
          }
        }
      }
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggles personal details editing mode.
   *
   * Switches the `isPersonalDetailsEditing` state between true and false,
   * enabling or disabling the personal details editing UI elements.
   *
   * @function togglePersonalDetailsEditing
   * @returns {void} Does not return a value.
   */
  const togglePersonalDetailsEditing = (): void => {
    setIsPersonalDetailsEditing(!isPersonalDetailsEditing);
  };

  /**
   * Exits personal details editing mode.
   *
   * Sets the `isPersonalDetailsEditing` state to false, hiding or disabling
   * the personal details editing UI elements.
   *
   * @function closePersonalDetailsEditing
   * @returns {void} Does not return a value.
   */
  const closePersonalDetailsEditing = (): void => {
    setIsPersonalDetailsEditing(false);
  };

  /**
   * Handles ZIP code lookup when user enters 5 digits
   */
  const handleZipCodeLookup = async (zipCode: string) => {
    if (zipCode.length === 5 && /^\d{5}$/.test(zipCode)) {
      try {
        console.log(`Looking up ZIP code: ${zipCode} for user address`);
        const response = await Axios.post(ApiEndpoints.GET_STATE_CITY_POSTAL, {
          postalCode: zipCode,
        });

        console.log('ZIP code lookup response:', response.data);

        if (response.data.status === 'success') {
          const addressData: AddressData = response.data.data;

          if (
            addressData.cities &&
            addressData.cities.length > 0 &&
            addressData.states &&
            addressData.states.length > 0 &&
            addressData.postalCodes &&
            addressData.postalCodes.length > 0
          ) {
            const city = addressData.cities[0];
            const state = addressData.states[0];
            const postalCode = addressData.postalCodes[0];

            console.log(city, state, postalCode);
            console.log(
              `Found city: ${city.cityName}, state: ${state.stateName}, postalCode: ${postalCode.postalCode} for ZIP: ${zipCode}`,
            );

            setUserAddress((prev) => ({
              ...prev,
              city: city,
              state: state,
              postalCode: postalCode,
            }));
            showToastMessage(
              `Address found: ${city.cityName}, ${state.stateName}`,
              'success',
            );
          } else {
            console.log(
              'No city/state/postalCode found for ZIP code:',
              zipCode,
            );
            showToastMessage('ZIP code not found', 'error');
          }
        } else {
          console.log('API response not successful:', response.data);
          showToastMessage('Failed to lookup ZIP code', 'error');
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          showToastMessage(
            error.response?.data?.message || 'Error fetching address data',
            'error',
          );
        }
      }
    }
  };

  /**
   * Handles user address field changes
   */
  const handleUserAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserAddress((prev) => {
      const updated = { ...prev };

      if (name === 'address1' || name === 'address2' || name === 'address3') {
        // Handle text address fields
        updated[name] = value;
      } else if (name === 'postalCode') {
        // Handle postal code - update the postalCode object
        updated.postalCode = { ...prev.postalCode, postalCode: value };
        // ZIP code lookup when 5 digits are entered
        handleZipCodeLookup(value);
      } else if (name === 'city') {
        // Handle city - update the city object
        updated.city = { ...prev.city, cityName: value };
      } else if (name === 'state') {
        // Handle state - update the state object
        updated.state = { ...prev.state, stateName: value };
      }

      return updated;
    });
  };

  /**
   * Handles user details field changes
   */
  const handleUserDetailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * Prepares address payload for API submission
   */
  const prepareAddressPayload = (address: Address) => ({
    address1: address.address1,
    address2: address.address2,
    address3: address.address3,
    stateId: address.state ? address.state.id : undefined,
    cityId: address.city ? address.city.id : undefined,
    postalCodeId: address.postalCode ? address.postalCode.id : undefined,
  });

  /**
   * Saves user address and details to the API
   */
  const saveUserDetails = async () => {
    try {
      const payload = {
        userAddress: prepareAddressPayload(userAddress),
        name: userDetails.name,
        phone: userDetails.phone,
      };

      await Axios.put(ApiEndpoints.CREATE_ADDRESS, payload);
      showToastMessage(responseTexts.ADDRESS_CREATE_SUCCESS, 'success');
      setIsPersonalDetailsEditing(false);

      // Refresh data
      await fetchUserAddress();
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }
  };

  /**
   * Toggles password editing mode.
   *
   * Switches the `isPasswordEditing` state between true and false,
   * enabling or disabling the password editing UI elements.
   *
   * @function togglePasswordEditing
   * @returns {void} Does not return a value.
   */
  const togglePasswordEditing = (): void => {
    setIsPasswordEditing(!isPasswordEditing);
  };

  /**
   * Exits password editing mode.
   *
   * Sets the `isPasswordEditing` state to false, hiding or disabling
   * the password editing UI elements.
   *
   * @function closePasswordEditing
   * @returns {void} Does not return a value.
   */
  const closePasswordEditing = (): void => {
    setIsPasswordEditing(false);
    setChangePasswordObject({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  /**
   * Toggles the visibility of a password input field.
   *
   * Inverts the visibility state of the specified password field
   * (`oldPassword`, `newPassword`, or `confirmPassword`) in the
   * `showPasswords` state object.
   *
   * @function togglePasswordVisibility
   * @param {'oldPassword' | 'newPassword' | 'confirmPassword'} field
   *        - The password field whose visibility should be toggled.
   * @returns {void} Does not return a value.
   */
  const togglePasswordVisibility = (
    field: 'oldPassword' | 'newPassword' | 'confirmPassword',
  ): void => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  /**
   * Validates password fields and updates error state.
   *
   * Uses ValidationService to validate each password field and updates
   * the passwordErrors state with any validation messages.
   *
   * @function validatePasswordFields
   * @returns {boolean} - Returns true if all fields are valid, false otherwise.
   */
  const validatePasswordFields = (): boolean => {
    const errors = {
      oldPassword:
        changePasswordObject.oldPassword.trim() === ''
          ? 'Current password is required'
          : '',
      newPassword: ValidationService.validateField(
        'newPassword',
        changePasswordObject.newPassword,
        changePasswordObject,
      ),
      confirmPassword: ValidationService.validateField(
        'confirmPassword',
        changePasswordObject.confirmPassword,
        changePasswordObject,
      ),
    };

    setPasswordErrors(errors);
    return Object.values(errors).every((error) => error === '');
  };

  /**
   * Handles password field changes and validates the field.
   *
   * Updates the changePasswordObject state and validates the changed field,
   * clearing any existing error for that field if it becomes valid.
   *
   * @function handlePasswordChange
   * @param {'oldPassword' | 'newPassword' | 'confirmPassword'} field - The field being changed.
   * @param {string} value - The new value for the field.
   * @returns {void}
   */
  const handlePasswordChange = (
    field: 'oldPassword' | 'newPassword' | 'confirmPassword',
    value: string,
  ): void => {
    setChangePasswordObject((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (passwordErrors[field]) {
      setPasswordErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  /**
   * Prepares and displays the confirmation prompt for a save action.
   *
   * Sets the type of confirmation to the provided `type` and makes the
   * confirmation dialog visible.
   *
   * @function handleSave
   * @param {ConfirmationType} type - The type of confirmation to display
   *                                  (e.g., password change, data update).
   * @returns {void} Does not return a value.
   */
  /**
   * Prepares and displays the confirmation dialog for a save action.
   *
   * Updates the `confirmationType` state to the given `type` and sets
   * `showConfirmation` to `true`, triggering the confirmation UI.
   *
   * @param {ConfirmationType} type - The type of action to confirm (e.g., password change, store update).
   * @returns {void} Does not return a value.
   */
  const handleSave = (type: ConfirmationType): void => {
    if (type === 'password') {
      if (!validatePasswordFields()) {
        return; // Don't show confirmation if validation fails
      }
    }
    setConfirmationType(type);
    setShowConfirmation(true);
  };

  /**
   * Handles form submission based on the confirmation type.
   *
   * Sends a POST request to the appropriate endpoint based on whether
   * it's a password change or personal details update.
   *
   * @async
   * @function handleSubmit
   * @returns {Promise<void>} Resolves when the request is completed.
   */
  const handleSubmit: () => Promise<void> = async () => {
    try {
      // Check if we're in password editing mode or personal details editing mode
      if (isPasswordEditing) {
        await Axios.post(ApiEndpoints.CHANGE_PASSWORD, changePasswordObject);
        setShowConfirmation(false);
        showToastMessage(responseTexts.CHANGE_PASSWORD_SUCCESS, 'success');
        // Reset password form after successful update
        setChangePasswordObject({
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setIsPasswordEditing(false);
      } else if (isPersonalDetailsEditing) {
        // Handle personal details save
        await saveUserDetails();
        setShowConfirmation(false);
      }
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }
  };

  /**
   * Cancels the current save action and hides the confirmation dialog.
   *
   * Sets the `showConfirmation` state to `false` to close the confirmation UI.
   *
   * @function cancelSave
   * @returns {void} Does not return a value.
   */
  const cancelSave = (): void => {
    setShowConfirmation(false);
  };

  return (
    <>
      {/* Personal Details Container */}
      <div className="flex flex-col rounded-lg p-4 gap-4 shadow-[0_1px_2px_0_#C5C9E2]">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-medium text-[#000A16]">
            Personal Details
          </h2>
          {!isPersonalDetailsEditing && (
            <button
              type="button"
              onClick={togglePersonalDetailsEditing}
              className="gap-2 rounded py-1 px-2 flex items-center text-[#004834]"
            >
              <svg
                width="18"
                height="17"
                viewBox="0 0 18 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="size-4"
              >
                <path
                  d="M12.3125 1.35449C13.0938 0.573242 14.375 0.573242 15.1562 1.35449L16.375 2.57324C17.1562 3.35449 17.1562 4.63574 16.375 5.41699L14.875 6.91699L10.8125 2.85449L12.3125 1.35449ZM14.1562 7.63574L6.875 14.917C6.5625 15.2295 6.15625 15.4795 5.71875 15.6045L1.9375 16.6982C1.6875 16.792 1.40625 16.7295 1.21875 16.5107C1 16.3232 0.9375 16.042 1 15.792L2.125 12.0107C2.25 11.5732 2.5 11.167 2.8125 10.8545L10.0938 3.57324L14.1562 7.63574Z"
                  fill="#004834"
                />
              </svg>
              <span className="font-bold text-base">Edit</span>
            </button>
          )}
        </div>
        <div className="flex flex-col gap-4">
          {/* Profile Photo & Name */}
          <div className="flex gap-8">
            <div className="flex flex-col gap-2">
              <h2 className="font-medium text-lg align-middle text-[#999DA2]">
                Profile Photo
              </h2>
              <div className="relative">
                <div className="relative">
                  <div className="h-[100px] w-[100px] flex items-center justify-center rounded-full bg-primary text-3xl text-white font-semibold border border-[#00000040]">
                    {initials}
                  </div>
                  {/* Do not remove this code. Need this for profile img upload */}
                  {/* <img
                      src="/Assets/ProfileImg.jpg"
                      alt="Profile"
                      className="h-[100px] w-[100px] border border-[#00000040] rounded-full object-cover"
                    /> */}
                  <button
                    type="button"
                    className="absolute bottom-0 right-4 bg-white rounded-full p-2 border border-[#0000001F] h-7 w-7 flex items-center justify-center"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 13"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.54688 1.97949H7.42969C7.92188 1.97949 8.34375 2.30762 8.48438 2.75293L8.74219 3.47949H10.5C11.3203 3.47949 12 4.15918 12 4.97949V10.9795C12 11.8232 11.3203 12.4795 10.5 12.4795H1.5C0.65625 12.4795 0 11.8232 0 10.9795V4.97949C0 4.15918 0.65625 3.47949 1.5 3.47949H3.23438L3.49219 2.75293C3.63281 2.30762 4.05469 1.97949 4.54688 1.97949ZM6 10.2295C7.24219 10.2295 8.25 9.22168 8.25 7.97949C8.25 6.7373 7.24219 5.72949 6 5.72949C4.73438 5.72949 3.75 6.7373 3.75 7.97949C3.75 9.22168 4.73438 10.2295 6 10.2295Z"
                        fill="#000A16"
                        fillOpacity="0.6"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div className="flex flex-col justify-center rounded-tr rounded-br py-2 gap-1 font-medium text-lg align-middle">
              <p className="font-medium text-lg align-middle text-[#656565]">
                Name
              </p>
              <p className="text-[#000A16]">
                {loading
                  ? 'Loading...'
                  : userDetails.name || 'No name available'}
              </p>
            </div>
          </div>
          <div className="border-b border-[#0000001F]"></div>
          {/* Address Details */}
          {!isPersonalDetailsEditing && (
            <div className="flex flex-col gap-6">
              <div className="flex gap-10">
                <div className="flex flex-col gap-0.5 w-full">
                  <h2 className="text-lg font-normal text-[#000A1666]">
                    Address line 1
                  </h2>
                  <p className="text-[#000A16] text-lg font-bold">
                    {loading ? 'Loading...' : userAddress.address1 || '-'}
                  </p>
                </div>
                <div className="flex flex-col gap-0.5 w-full">
                  <h2 className="text-lg font-normal text-[#000A1666]">
                    Address line 2
                  </h2>
                  <p className="text-[#000A16] text-lg font-bold">
                    {loading ? 'Loading...' : userAddress.address2 || '-'}
                  </p>
                </div>
              </div>
              <div className="flex gap-10">
                <div className="flex flex-col gap-0.5 w-full">
                  <h2 className="text-lg font-normal text-[#000A1666]">
                    Address line 3
                  </h2>
                  <p className="text-[#000A16] text-lg font-bold">
                    {loading ? 'Loading...' : userAddress.address3 || '-'}
                  </p>
                </div>
                <div className="flex flex-col gap-0.5 w-full">
                  <h2 className="text-lg font-normal text-[#000A1666]">City</h2>
                  <p className="text-[#000A16] text-lg font-bold">
                    {loading ? 'Loading...' : userAddress.city.cityName || '-'}
                  </p>
                </div>
              </div>
              <div className="flex gap-10">
                <div className="flex flex-col gap-0.5 w-full">
                  <h2 className="text-lg font-normal text-[#000A1666]">
                    State
                  </h2>
                  <p className="text-[#000A16] text-lg font-bold">
                    {loading
                      ? 'Loading...'
                      : userAddress.state.stateName || '-'}
                  </p>
                </div>
                <div className="flex flex-col gap-0.5 w-full">
                  <h2 className="text-lg font-normal text-[#000A1666]">
                    ZipCode
                  </h2>
                  <p className="text-[#000A16] text-lg font-bold">
                    {loading
                      ? 'Loading...'
                      : userAddress.postalCode.postalCode || '-'}
                  </p>
                </div>
              </div>
            </div>
          )}
          {/* Address Details Input */}
          {isPersonalDetailsEditing && (
            <div className="flex flex-col gap-6">
              <div className="flex gap-10">
                <div className="flex flex-col gap-0.5 w-full">
                  <label
                    htmlFor="address1"
                    className="text-lg font-normal text-[#000A1666]"
                  >
                    Address line 1
                  </label>
                  <input
                    name="address1"
                    className="text-[#000A16] text-lg font-bold h-[41px] rounded-md border border-[#00000080] p-3 gap-2.5"
                    value={userAddress.address1}
                    onChange={handleUserAddressChange}
                  />
                </div>
                <div className="flex flex-col gap-0.5 w-full">
                  <label
                    htmlFor="address2"
                    className="text-lg font-normal text-[#000A1666]"
                  >
                    Address line 2
                  </label>
                  <input
                    name="address2"
                    className="text-[#000A16] text-lg font-bold h-[41px] rounded-md border border-[#00000080] p-3 gap-2.5"
                    value={userAddress.address2}
                    onChange={handleUserAddressChange}
                  />
                </div>
              </div>
              <div className="flex gap-10">
                <div className="flex flex-col gap-0.5 w-full">
                  <label
                    htmlFor="address3"
                    className="text-lg font-normal text-[#000A1666]"
                  >
                    Address line 3
                  </label>
                  <input
                    name="address3"
                    className="text-[#000A16] text-lg font-bold h-[41px] rounded-md border border-[#00000080] p-3 gap-2.5"
                    value={userAddress.address3}
                    onChange={handleUserAddressChange}
                  />
                </div>
                <div className="flex flex-col gap-0.5 w-full">
                  <label
                    htmlFor="city"
                    className="text-lg font-normal text-[#000A1666]"
                  >
                    City
                  </label>
                  <input
                    name="city"
                    readOnly
                    className="text-[#000A16] text-lg font-bold h-[41px] rounded-md border border-[#00000080] p-3 gap-2.5 bg-gray-100 cursor-not-allowed"
                    value={userAddress.city.cityName}
                    onChange={handleUserAddressChange}
                  />
                </div>
              </div>
              <div className="flex gap-10">
                <div className="flex flex-col gap-0.5 w-full">
                  <label
                    htmlFor="state"
                    className="text-lg font-normal text-[#000A1666]"
                  >
                    State
                  </label>
                  <input
                    name="state"
                    readOnly
                    className="text-[#000A16] text-lg font-bold h-[41px] rounded-md border border-[#00000080] p-3 gap-2.5 bg-gray-100 cursor-not-allowed"
                    value={userAddress.state.stateName}
                    onChange={handleUserAddressChange}
                  />
                </div>
                <div className="flex flex-col gap-0.5 w-full">
                  <label
                    htmlFor="postalCode"
                    className="text-lg font-normal text-[#000A1666]"
                  >
                    ZipCode
                  </label>
                  <input
                    name="postalCode"
                    className="text-[#000A16] text-lg font-bold h-[41px] rounded-md border border-[#00000080] p-3 gap-2.5"
                    value={userAddress.postalCode.postalCode}
                    onChange={handleUserAddressChange}
                  />
                </div>
              </div>
            </div>
          )}
          <div className="border-b border-[#0000001F]"></div>
          {/* Contact Details */}
          <div className="flex flex-col gap-2">
            <h2 className="gap-2 py-1 text-xl font-medium text-[#000A16]">
              Contact Details
            </h2>
            <div className="flex gap-6">
              <div className="flex flex-col gap-0.5 w-full">
                <h2 className="text-lg font-normal text-[#000A1666]">Email</h2>
                <p className="text-[#000A16] text-lg font-bold">
                  {loading
                    ? 'Loading...'
                    : userDetails.email || 'No email available'}
                </p>
              </div>
              {!isPersonalDetailsEditing && (
                <div className="flex flex-col gap-0.5 w-full">
                  <h2 className="text-lg font-normal text-[#000A1666]">
                    Phone Number
                  </h2>
                  <p className="text-[#000A16] text-lg font-bold">-</p>
                </div>
              )}
              {/* Phone Number Input */}
              {isPersonalDetailsEditing && (
                <div className="flex flex-col gap-0.5 w-full">
                  <label
                    htmlFor="phone"
                    className="text-lg font-normal text-[#000A1666]"
                  >
                    Phone Number
                  </label>
                  <input
                    name="phone"
                    className="text-[#000A16] text-sm font-medium h-[41px] rounded-md border border-[#00000080] p-3 gap-2.5"
                    value={userDetails.phone || ''}
                    onChange={handleUserDetailsChange}
                  />
                </div>
              )}
            </div>
          </div>
          {/* Save changes button */}
          {isPersonalDetailsEditing && (
            <div className="flex justify-end gap-4 h-10">
              <button
                type="button"
                onClick={closePersonalDetailsEditing}
                className="rounded gap-2 h-10 w-[71px] p-2"
              >
                <span className="text-[#004834] font-bold text-base">
                  Cancel
                </span>
              </button>
              <button
                type="button"
                onClick={() => handleSave('personal')}
                className="rounded py-1.5 px-6 gap-[5.69px] h-10 w-[92px] bg-[#004834]"
              >
                <span className="text-white text-lg font-semibold text-center">
                  Save
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Store Details */}
      <div className="flex flex-col rounded-lg p-4 shadow-[0_1px_2px_0_#C5C9E2] gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex gap-2 py-1">
            <h1 className="font-bold text-xl text-[#000A16]">Store Details</h1>
          </div>
          <div className="flex gap-10">
            <div className="flex flex-col gap-0.5 w-full">
              <h2 className="text-lg font-normal text-[#000A1666]">
                Company Name *
              </h2>
              <p className="text-[#000A16] text-lg font-bold">
                {loading
                  ? 'Loading...'
                  : storeDetails.companyName || 'No company name available'}
              </p>
            </div>
            <div className="w-1/2"></div>
          </div>
          <div className="flex gap-10">
            <div className="flex flex-col gap-0.5 w-full">
              <h2 className="text-lg font-normal text-[#000A1666]">
                Address line 1
              </h2>
              <p className="text-[#000A16] text-lg font-bold">
                {loading ? 'Loading...' : storeDetails.address.address1 || '-'}
              </p>
            </div>
            <div className="flex flex-col gap-0.5 w-full">
              <h2 className="text-lg font-normal text-[#000A1666]">
                Address line 2
              </h2>
              <p className="text-[#000A16] text-lg font-bold">
                {loading ? 'Loading...' : storeDetails.address.address2 || '-'}
              </p>
            </div>
          </div>
          <div className="flex gap-10">
            <div className="flex flex-col gap-0.5 w-full">
              <h2 className="text-lg font-normal text-[#000A1666]">
                Address line 3
              </h2>
              <p className="text-[#000A16] text-lg font-bold">
                {loading ? 'Loading...' : storeDetails.address.address3 || '-'}
              </p>
            </div>
            <div className="flex flex-col gap-0.5 w-full">
              <h2 className="text-lg font-normal text-[#000A1666]">City</h2>
              <p className="text-[#000A16] text-lg font-bold">
                {loading
                  ? 'Loading...'
                  : storeDetails.address.city.cityName || '-'}
              </p>
            </div>
          </div>
          <div className="flex gap-10">
            <div className="flex flex-col gap-0.5 w-full">
              <h2 className="text-lg font-normal text-[#000A1666]">State</h2>
              <p className="text-[#000A16] text-lg font-bold">
                {loading
                  ? 'Loading...'
                  : storeDetails.address.state.stateName || '-'}
              </p>
            </div>
            <div className="flex flex-col gap-0.5 w-full">
              <h2 className="text-lg font-normal text-[#000A1666]">ZipCode</h2>
              <p className="text-[#000A16] text-lg font-bold">
                {loading
                  ? 'Loading...'
                  : storeDetails.address.postalCode.postalCode || '-'}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Password & Security */}
      <div className="flex flex-col rounded-lg pb-4 shadow-[0_1px_2px_0_#C5C9E2]">
        <div
          className={`flex flex-col justify-between py-4 rounded-tr-2xl rounded-tl-2xl ${isPasswordEditing ? 'px-4' : 'px-6'}`}
        >
          <h2 className="text-xl font-medium text-[#000A16]">
            Password & Security
          </h2>
          {isPasswordEditing && (
            <p className="font-normal text-base text-[#000A1666]">
              Please enter your current password to change your password
            </p>
          )}{' '}
        </div>
        {/*Change password button  */}
        {!isPasswordEditing && (
          <div className="flex">
            <div className="flex gap-4 ml-px w-full">
              <div className="pr-4 pl-6 gap-3 w-full">
                <div className="flex justify-between gap-3">
                  <div className="flex gap-2">
                    <div className="flex items-center justify-center w-4 h-7">
                      <svg
                        width="16"
                        height="19"
                        viewBox="0 0 16 19"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-7"
                      >
                        <path
                          d="M2.8125 7.72949V6.04199C2.8125 3.26465 5.0625 0.979492 7.875 0.979492C10.6523 0.979492 12.9375 3.26465 12.9375 6.04199V7.72949H13.5C14.7305 7.72949 15.75 8.74902 15.75 9.97949V16.7295C15.75 17.9951 14.7305 18.9795 13.5 18.9795H2.25C0.984375 18.9795 0 17.9951 0 16.7295V9.97949C0 8.74902 0.984375 7.72949 2.25 7.72949H2.8125ZM5.0625 7.72949H10.6875V6.04199C10.6875 4.49512 9.42188 3.22949 7.875 3.22949C6.29297 3.22949 5.0625 4.49512 5.0625 6.04199V7.72949Z"
                          fill="black"
                          fillOpacity="0.5"
                        />
                      </svg>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <h2 className="text-lg font-medium text-[#000A16] align-middle">
                        Account Password
                      </h2>
                      <p className="text-[#000A1666] text-base font-normal">
                        A Secure password helps protect your account
                      </p>
                    </div>
                  </div>
                  <div className="gap-2 rounded">
                    <div className="p-2 gap-2.5">
                      <button
                        type="button"
                        onClick={togglePasswordEditing}
                        className="text-[#004834] text-base font-bold"
                      >
                        Change Password
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Password & Security Form */}
        {isPasswordEditing && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col px-4 gap-4">
              <div className="flex gap-6 items-start">
                <label
                  htmlFor="current_password"
                  className="text-lg font-normal text-[#000A1666] min-w-[220px]"
                >
                  Current Password
                </label>

                <div className="flex flex-col w-[404px]">
                  <div className="relative h-12">
                    <input
                      type={showPasswords.oldPassword ? 'text' : 'password'}
                      name="current_password"
                      value={changePasswordObject.oldPassword}
                      onChange={(e) =>
                        handlePasswordChange('oldPassword', e.target.value)
                      }
                      className={`h-12 w-full rounded-md border p-3 ${
                        passwordErrors.oldPassword
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-[#00000080] focus:border-[#004834]'
                      }`}
                    />
                    <span
                      className="absolute right-4 top-3 cursor-pointer"
                      onClick={() => togglePasswordVisibility('oldPassword')}
                    >
                      {showPasswords.oldPassword ? (
                        /* Eye open SVG */
                        <svg
                          width="21"
                          height="16"
                          viewBox="0 0 21 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 2C14.3125 2 17.6875 4.25 19.375 7.5625C19.4375 7.6875 19.5 7.84375 19.5 8.03125C19.5 8.1875 19.4375 8.34375 19.375 8.46875C17.6875 11.7812 14.3125 14 10.5 14C6.65625 14 3.28125 11.7812 1.59375 8.46875C1.53125 8.34375 1.5 8.1875 1.5 8C1.5 7.84375 1.53125 7.6875 1.59375 7.5625C3.28125 4.25 6.65625 2 10.5 2ZM10.5 12.5C12.375 12.5 13.9062 11 13.9062 9.125C13.9062 7.28125 12.375 5.75 10.5 5.75C8.59375 5.75 7.0625 7.28125 7.0625 9.125C7.0625 11 8.59375 12.5 10.5 12.5ZM10.5 11C9.40625 11 8.5625 10.1562 8.5625 9.0625C8.5625 7.96875 9.40625 7.125 10.5 7.125C11.5625 7.125 12.4062 7.96875 12.4062 9.0625C12.4062 10.1562 11.5625 11 10.5 11Z"
                            fill="black"
                            fillOpacity="0.5"
                          />
                        </svg>
                      ) : (
                        /* Eye closed SVG */
                        <svg
                          width="21"
                          height="16"
                          viewBox="0 0 21 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M20.8125 14.7188C20.9062 14.8125 21 14.9688 21 15.125C21 15.25 20.9375 15.3438 20.875 15.4375L20.5625 15.8125C20.4688 15.9375 20.3438 16 20.1562 16C20.0625 16 19.9375 15.9688 19.875 15.9062L1.1875 1.28125C1.0625 1.21875 0.96875 1.0625 0.96875 0.90625C0.96875 0.78125 1.03125 0.6875 1.09375 0.59375L1.40625 0.1875C1.5 0.09375 1.625 0.03125 1.8125 0.03125C1.90625 0.03125 2.03125 0.0625 2.125 0.125L20.8125 14.7188ZM10.25 4.59375C10.5 4.53125 10.75 4.53125 11 4.5C12.875 4.5 14.4062 6 14.4688 7.875L10.25 4.59375ZM11.7188 11.4375C11.4688 11.5 11.2188 11.5 11 11.5C9.09375 11.5 7.5625 10.0312 7.5 8.15625L11.7188 11.4375ZM11 3.5C10.375 3.5 9.75 3.59375 9.15625 3.75L7.71875 2.59375C8.75 2.21875 9.84375 2 11 2C14.8125 2 18.1875 4.25 19.875 7.5625C19.9375 7.6875 20 7.84375 20 8.03125C20 8.1875 19.9375 8.34375 19.875 8.46875C19.4375 9.34375 18.875 10.125 18.2188 10.8125L17.0312 9.90625C17.5938 9.34375 18.0312 8.71875 18.4062 8C16.9062 5.21875 14.0625 3.5 11 3.5ZM11 12.5C11.5938 12.5 12.2188 12.4375 12.8125 12.2812L14.25 13.4375C13.2188 13.7812 12.125 14 11 14C7.15625 14 3.78125 11.7812 2.09375 8.46875C2.03125 8.34375 2 8.1875 2 8C2 7.84375 2.03125 7.6875 2.09375 7.5625C2.53125 6.6875 3.09375 5.90625 3.75 5.21875L4.9375 6.125C4.375 6.6875 3.9375 7.3125 3.5625 8C5.0625 10.7812 7.90625 12.5 11 12.5Z"
                            fill="black"
                            fillOpacity="0.5"
                          />
                        </svg>
                      )}
                    </span>
                  </div>

                  {passwordErrors.oldPassword && (
                    <span className="text-red-500 text-sm mt-1">
                      {passwordErrors.oldPassword}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <label
                  htmlFor="new_password"
                  className="text-lg font-normal text-[#000A1666] min-w-[220px]"
                >
                  New Password
                </label>

                <div className="flex flex-col w-[404px]">
                  <div className="relative h-12">
                    <input
                      type={showPasswords.newPassword ? 'text' : 'password'}
                      value={changePasswordObject.newPassword}
                      onChange={(e) =>
                        handlePasswordChange('newPassword', e.target.value)
                      }
                      name="new_password"
                      id="new_password"
                      className={`h-12 w-full rounded-md border p-3 ${
                        passwordErrors.newPassword
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-[#00000080] focus:border-[#004834]'
                      }`}
                    />
                    <span
                      className="absolute right-4 top-3 cursor-pointer"
                      onClick={() => togglePasswordVisibility('newPassword')}
                    >
                      {showPasswords.newPassword ? (
                        /* Eye open icon */
                        <svg
                          width="21"
                          height="16"
                          viewBox="0 0 21 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 2C14.3125 2 17.6875 4.25 19.375 7.5625C19.4375 7.6875 19.5 7.84375 19.5 8.03125C19.5 8.1875 19.4375 8.34375 19.375 8.46875C17.6875 11.7812 14.3125 14 10.5 14C6.65625 14 3.28125 11.7812 1.59375 8.46875C1.53125 8.34375 1.5 8.1875 1.5 8C1.5 7.84375 1.53125 7.6875 1.59375 7.5625C3.28125 4.25 6.65625 2 10.5 2ZM10.5 12.5C12.375 12.5 13.9062 11 13.9062 9.125C13.9062 7.28125 12.375 5.75 10.5 5.75C8.59375 5.75 7.0625 7.28125 7.0625 9.125C7.0625 11 8.59375 12.5 10.5 12.5ZM10.5 11C9.40625 11 8.5625 10.1562 8.5625 9.0625C8.5625 7.96875 9.40625 7.125 10.5 7.125C11.5625 7.125 12.4062 7.96875 12.4062 9.0625C12.4062 10.1562 11.5625 11 10.5 11Z"
                            fill="black"
                            fillOpacity="0.5"
                          />
                        </svg>
                      ) : (
                        /* Eye closed icon */
                        <svg
                          width="21"
                          height="16"
                          viewBox="0 0 21 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M20.8125 14.7188C20.9062 14.8125 21 14.9688 21 15.125C21 15.25 20.9375 15.3438 20.875 15.4375L20.5625 15.8125C20.4688 15.9375 20.3438 16 20.1562 16C20.0625 16 19.9375 15.9688 19.875 15.9062L1.1875 1.28125C1.0625 1.21875 0.96875 1.0625 0.96875 0.90625C0.96875 0.78125 1.03125 0.6875 1.09375 0.59375L1.40625 0.1875C1.5 0.09375 1.625 0.03125 1.8125 0.03125C1.90625 0.03125 2.03125 0.0625 2.125 0.125L20.8125 14.7188ZM10.25 4.59375C10.5 4.53125 10.75 4.53125 11 4.5C12.875 4.5 14.4062 6 14.4688 7.875L10.25 4.59375ZM11.7188 11.4375C11.4688 11.5 11.2188 11.5 11 11.5C9.09375 11.5 7.5625 10.0312 7.5 8.15625L11.7188 11.4375ZM11 3.5C10.375 3.5 9.75 3.59375 9.15625 3.75L7.71875 2.59375C8.75 2.21875 9.84375 2 11 2C14.8125 2 18.1875 4.25 19.875 7.5625C19.9375 7.6875 20 7.84375 20 8.03125C20 8.1875 19.9375 8.34375 19.875 8.46875C19.4375 9.34375 18.875 10.125 18.2188 10.8125L17.0312 9.90625C17.5938 9.34375 18.0312 8.71875 18.4062 8C16.9062 5.21875 14.0625 3.5 11 3.5ZM11 12.5C11.5938 12.5 12.2188 12.4375 12.8125 12.2812L14.25 13.4375C13.2188 13.7812 12.125 14 11 14C7.15625 14 3.78125 11.7812 2.09375 8.46875C2.03125 8.34375 2 8.1875 2 8C2 7.84375 2.03125 7.6875 2.09375 7.5625C2.53125 6.6875 3.09375 5.90625 3.75 5.21875L4.9375 6.125C4.375 6.6875 3.9375 7.3125 3.5625 8C5.0625 10.7812 7.90625 12.5 11 12.5Z"
                            fill="black"
                            fillOpacity="0.5"
                          />
                        </svg>
                      )}
                    </span>
                  </div>

                  {passwordErrors.newPassword && (
                    <span className="text-red-500 text-sm mt-1">
                      {passwordErrors.newPassword}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <label
                  htmlFor="confirm_new_password"
                  className="text-lg font-normal text-[#000A1666] min-w-[220px]"
                >
                  Confirm New Password
                </label>

                <div className="flex flex-col w-[404px]">
                  <div className="relative h-12">
                    <input
                      type={showPasswords.confirmPassword ? 'text' : 'password'}
                      value={changePasswordObject.confirmPassword}
                      onChange={(e) =>
                        handlePasswordChange('confirmPassword', e.target.value)
                      }
                      name="confirm_new_password"
                      id="confirm_new_password"
                      className={`h-12 w-full rounded-md border p-3 ${
                        passwordErrors.confirmPassword
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-[#00000080] focus:border-[#004834]'
                      }`}
                    />
                    <span
                      className="absolute right-4 top-3 cursor-pointer"
                      onClick={() =>
                        togglePasswordVisibility('confirmPassword')
                      }
                    >
                      {showPasswords.confirmPassword ? (
                        // Eye open icon
                        <svg
                          width="21"
                          height="16"
                          viewBox="0 0 21 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 2C14.3125 2 17.6875 4.25 19.375 7.5625C19.4375 7.6875 19.5 7.84375 19.5 8.03125C19.5 8.1875 19.4375 8.34375 19.375 8.46875C17.6875 11.7812 14.3125 14 10.5 14C6.65625 14 3.28125 11.7812 1.59375 8.46875C1.53125 8.34375 1.5 8.1875 1.5 8C1.5 7.84375 1.53125 7.6875 1.59375 7.5625C3.28125 4.25 6.65625 2 10.5 2ZM10.5 12.5C12.375 12.5 13.9062 11 13.9062 9.125C13.9062 7.28125 12.375 5.75 10.5 5.75C8.59375 5.75 7.0625 7.28125 7.0625 9.125C7.0625 11 8.59375 12.5 10.5 12.5ZM10.5 11C9.40625 11 8.5625 10.1562 8.5625 9.0625C8.5625 7.96875 9.40625 7.125 10.5 7.125C11.5625 7.125 12.4062 7.96875 12.4062 9.0625C12.4062 10.1562 11.5625 11 10.5 11Z"
                            fill="black"
                            fillOpacity="0.5"
                          />
                        </svg>
                      ) : (
                        // Eye closed icon
                        <svg
                          width="21"
                          height="16"
                          viewBox="0 0 21 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M20.8125 14.7188C20.9062 14.8125 21 14.9688 21 15.125C21 15.25 20.9375 15.3438 20.875 15.4375L20.5625 15.8125C20.4688 15.9375 20.3438 16 20.1562 16C20.0625 16 19.9375 15.9688 19.875 15.9062L1.1875 1.28125C1.0625 1.21875 0.96875 1.0625 0.96875 0.90625C0.96875 0.78125 1.03125 0.6875 1.09375 0.59375L1.40625 0.1875C1.5 0.09375 1.625 0.03125 1.8125 0.03125C1.90625 0.03125 2.03125 0.0625 2.125 0.125L20.8125 14.7188ZM10.25 4.59375C10.5 4.53125 10.75 4.53125 11 4.5C12.875 4.5 14.4062 6 14.4688 7.875L10.25 4.59375ZM11.7188 11.4375C11.4688 11.5 11.2188 11.5 11 11.5C9.09375 11.5 7.5625 10.0312 7.5 8.15625L11.7188 11.4375ZM11 3.5C10.375 3.5 9.75 3.59375 9.15625 3.75L7.71875 2.59375C8.75 2.21875 9.84375 2 11 2C14.8125 2 18.1875 4.25 19.875 7.5625C19.9375 7.6875 20 7.84375 20 8.03125C20 8.1875 19.9375 8.34375 19.875 8.46875C19.4375 9.34375 18.875 10.125 18.2188 10.8125L17.0312 9.90625C17.5938 9.34375 18.0312 8.71875 18.4062 8C16.9062 5.21875 14.0625 3.5 11 3.5ZM11 12.5C11.5938 12.5 12.2188 12.4375 12.8125 12.2812L14.25 13.4375C13.2188 13.7812 12.125 14 11 14C7.15625 14 3.78125 11.7812 2.09375 8.46875C2.03125 8.34375 2 8.1875 2 8C2 7.84375 2.03125 7.6875 2.09375 7.5625C2.53125 6.6875 3.09375 5.90625 3.75 5.21875L4.9375 6.125C4.375 6.6875 3.9375 7.3125 3.5625 8C5.0625 10.7812 7.90625 12.5 11 12.5Z"
                            fill="black"
                            fillOpacity="0.5"
                          />
                        </svg>
                      )}
                    </span>
                  </div>

                  {passwordErrors.confirmPassword && (
                    <span className="text-red-500 text-sm mt-1">
                      {passwordErrors.confirmPassword}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {/* Save changes button */}
            <div className="flex justify-end px-4 gap-4 h-10">
              <button
                type="button"
                className="rounded gap-2 h-10 w-[71px] p-2"
                onClick={closePasswordEditing}
              >
                <span className="text-[#004834] font-bold text-base">
                  Cancel
                </span>
              </button>
              <button
                type="button"
                onClick={() => handleSave('password')}
                className="rounded py-1.5 px-6 gap-[5.69px] h-10 w-[92px] bg-[#004834]"
              >
                <span className="text-white text-lg font-semibold text-center">
                  Save
                </span>
              </button>
            </div>
          </div>
        )}
      </div>
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <SaveChangesConfirmation
            onClose={cancelSave}
            onConfirm={() => handleSubmit()}
          />
        </div>
      )}
    </>
  );
};
